import { useEffect, useState } from 'react';
import { X, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import sensorRulesService, { type RuleEvent, type SensorRule } from '../../services/sensorRules';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

interface Props {
  rule: SensorRule;
  onClose: () => void;
}

export default function SensorRuleEventsModal({ rule, onClose }: Props) {
  const [events, setEvents] = useState<RuleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const orgTz = useOrgTimezone();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    sensorRulesService.listEvents(rule.id, 50)
      .then(e => { if (mounted) setEvents(e); })
      .catch(() => { if (mounted) setError('Failed to load events'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [rule.id]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
          <h3 className="font-semibold">Event history: {rule.name}</h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="text-sm text-gray-400 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && events.length === 0 && (
            <div className="text-sm text-gray-400 italic">No events yet.</div>
          )}
          <ul className="divide-y divide-gray-100">
            {events.map(ev => (
              <li key={ev.id} className="py-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{formatDateTimeInTz(ev.fired_at, orgTz)}</span>
                  {ev.trigger_value !== null && (
                    <span className="text-xs text-gray-500">
                      value = {Number(ev.trigger_value).toFixed(2)}
                    </span>
                  )}
                </div>
                <ul className="mt-1 space-y-0.5">
                  {ev.actions_run.map((a, i) => (
                    <li key={i} className="text-xs flex items-center gap-1.5">
                      {a.ok
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                        : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                      <span className="font-mono">{a.type}</span>
                      {!a.ok && a.error && <span className="text-red-600">— {a.error}</span>}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
